import { Link } from "react-router-dom";
import { Profile } from "../../../services/profile/profileModel";
import { useMemo } from "react";
import { cx } from "../../../react-helpers/css";
import { useTranslation } from "react-i18next";
import {
  useCountries,
  useLangs,
} from "../../../services/profile/profileService";
import { useProgressiveImage } from "../../../services/imageService";
import SubmitButton from "../../../components/SubmitButton";
import { Tooltip } from "react-tooltip";

const ProfileCard = ({
  profile,
  locked,
  canUpgrade,
  onUpgrade,
  isArchived,
  onArchive,
  onUnarchive,
  isFavorited,
  onFavorite,
  onUnfavorite,
  onDelete,
}: {
  profile: Profile;
  /** Free-plan quota: the video cannot be watched without subscribing */
  locked?: boolean;
  /** Whether the current user can reach the subscription page (org admin) */
  canUpgrade?: boolean;
  /** Opens the subscribe dialog in place instead of navigating to the subscription page */
  onUpgrade?: () => void;
  isArchived?: boolean;
  onArchive?: () => void;
  onUnarchive?: () => void;
  isFavorited?: boolean;
  onFavorite?: () => Promise<void> | void;
  onUnfavorite?: () => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
}) => {
  const { t } = useTranslation();
  const countries = useCountries();
  const langs = useLangs();
  const country = useMemo(
    () => countries.find((c) => c[0] === profile.country)?.[1],
    [profile.country, countries],
  );
  const language = useMemo(
    () => langs.find((c) => c[0] === profile.lang)?.[1],
    [profile.lang, langs],
  );

  const thumbnail = useProgressiveImage(
    profile.thumbnail ?? profile.thumbnailUrl,
  );

  return (
    <Link
      className={cx([
        "profile-card --block",
        !!profile.disabledDate && "disabled",
        !!profile.user && "--org",
      ])}
      to={
        locked
          ? "/organization/pro/subscription"
          : profile.user
            ? `/profile-share/${profile.uid}`
            : `../profiles/${profile.id}`
      }
      onClick={
        locked
          ? (e) => {
              if (!canUpgrade) {
                e.preventDefault();
              } else if (onUpgrade) {
                e.preventDefault();
                onUpgrade();
              }
            }
          : undefined
      }
    >
      {!!profile.user && (
        <div className="card--actions">
          {!isArchived
            ? !!onArchive && (
                <button
                  className="btn--archive"
                  data-tooltip-id={`archive-${profile.uid}`}
                  data-tooltip-content={t("candidate:tooltips.ARCHIVE")}
                  onClick={(e) => {
                    e.preventDefault();
                    onArchive();
                  }}
                />
              )
            : !!onUnarchive && (
                <button
                  className="btn--unarchive"
                  data-tooltip-id={`unarchive-${profile.uid}`}
                  data-tooltip-content={t("candidate:tooltips.UNARCHIVE")}
                  onClick={(e) => {
                    e.preventDefault();
                    onUnarchive();
                  }}
                />
              )}

          {isFavorited !== undefined &&
            (!isFavorited
              ? !!onFavorite && (
                  <SubmitButton
                    type="button"
                    className="btn--favorite"
                    data-tooltip-id={`favorite-${profile.uid}`}
                    data-tooltip-content={t("candidate:tooltips.FAVORITE")}
                    onClick={(e) => {
                      e.preventDefault();
                      return onFavorite();
                    }}
                  />
                )
              : !!onUnfavorite && (
                  <SubmitButton
                    type="button"
                    data-tooltip-id={`unfavorite-${profile.uid}`}
                    data-tooltip-content={t("candidate:tooltips.UNFAVORITE")}
                    className="btn--unfavorite"
                    onClick={(e) => {
                      e.preventDefault();
                      return onUnfavorite();
                    }}
                  />
                ))}

          {!!onDelete && (
            <SubmitButton
              type="button"
              className="btn--delete"
              data-tooltip-id={`delete-${profile.uid}`}
              data-tooltip-content={t("candidate:tooltips.DELETE")}
              onClick={(e) => {
                e.preventDefault();
                return onDelete?.();
              }}
            />
          )}
        </div>
      )}
      <div className="card_image_container">
        <canvas
          className="card_image_bg"
          width="16"
          height="9"
          style={{
            backgroundImage: thumbnail ? `url(${thumbnail})` : undefined,
          }}
        ></canvas>
        <canvas
          className="card_image"
          style={{
            backgroundImage: thumbnail ? `url(${thumbnail})` : undefined,
          }}
        />
      </div>

      <div className="card_body">
        <p className="profile_name">
          {profile.user
            ? `${profile.user?.firstname} ${profile.user?.lastname}`
            : profile.name}
        </p>
        <div className="profile_flags">
          <p className="chip">{country}</p>
          <p className="chip">{language}</p>
        </div>
      </div>
      {!!profile.disabledDate && (
        <div className="disabled-overlay">
          {t("candidate:profile.card.DISABLED")}
        </div>
      )}
      {locked && !profile.disabledDate && (
        <div className="locked-overlay">
          <div>{t("organization:candidates-profiles.LOCKED_PROFILE")}</div>
          {canUpgrade ? (
            <span className="btn--2">
              {t("organization:candidates-profiles.UPGRADE_CTA")}
            </span>
          ) : (
            <div className="body--30">
              {t("organization:candidates-profiles.LOCKED_CONTACT_ADMIN")}
            </div>
          )}
        </div>
      )}
      <Tooltip id={`archive-${profile.uid}`} />
      <Tooltip id={`unarchive-${profile.uid}`} />
      <Tooltip id={`favorite-${profile.uid}`} />
      <Tooltip id={`unfavorite-${profile.uid}`} />
      <Tooltip id={`delete-${profile.uid}`} />
    </Link>
  );
};

export default ProfileCard;
